import React, { useEffect, useRef, useState, useCallback } from 'react';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';

// Get status configuration from SCMejora status formats
const getStatusFromEstado = (estado) => {
  if (!estado) {
    return {
      color: [107, 114, 128],    // Gris neutro
      label: 'Sin Estado',
      shape: 'circle'
    };
  }
  
  const estadoLower = estado.toLowerCase();
  
  // Completed / Finalized
  if (estadoLower.includes('finalizada') || estadoLower.includes('completada') || estadoLower.includes('resuelta') || estadoLower.includes('cerrada')) {
    return {
      color: [0, 128, 64],       // Verde Valoriza
      label: 'Completada',
      shape: 'circle'
    };
  }
  
  // In Progress / En Ejecución
  if (estadoLower.includes('ejecucion') || estadoLower.includes('progreso')) {
    return {
      color: [234, 136, 12],     // Naranja
      label: 'En Progreso',
      shape: 'diamond'
    };
  }
  
  // En Reparación (but not finalizada)
  if (estadoLower.includes('reparacion') && !estadoLower.includes('finalizada')) {
    return {
      color: [234, 136, 12],     // Naranja
      label: 'En Reparación',
      shape: 'diamond'
    };
  }
  
  // Pending
  if (estadoLower.includes('pendiente')) {
    return {
      color: [156, 163, 175],    // Gris
      label: 'Pendiente',
      shape: 'square'
    };
  }
  
  // Rejected
  if (estadoLower.includes('rechazada')) {
    return {
      color: [190, 24, 93],      // Rosa/Magenta
      label: 'Rechazada',
      shape: 'x'
    };
  }
  
  // Blocked
  if (estadoLower.includes('bloqueada')) {
    return {
      color: [190, 24, 93],      // Rosa/Magenta
      label: 'Bloqueada',
      shape: 'x'
    };
  }
  
  // Open
  if (estadoLower.includes('abierta')) {
    return {
      color: [220, 38, 38],      // Rojo
      label: 'Abierta',
      shape: 'circle'
    };
  }
  
  // Default
  return {
    color: [107, 114, 128],    // Gris neutro
    label: estado,
    shape: 'circle'
  };
};

export default function MapViewComponent({ tasks, selectedTask, onSelectTask }) {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const graphicsLayerRef = useRef(null);
  const highlightLayerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  
  // Store callback in ref to avoid recreating map on callback changes
  const onSelectTaskRef = useRef(onSelectTask);
  useEffect(() => {
    onSelectTaskRef.current = onSelectTask;
  }, [onSelectTask]);

  // Initialize map (runs once)
  useEffect(() => {
    if (!mapDiv.current) return;

    const webMap = new WebMap({
      basemap: 'streets-navigation-vector'
    });

    const view = new MapView({
      container: mapDiv.current,
      map: webMap,
      center: [-16.2518467, 28.4636296],
      zoom: 13,
      popup: {
        dockEnabled: false,
        dockOptions: {
          buttonEnabled: false,
          breakpoint: false
        }
      }
    });

    // Create graphics layers
    const graphicsLayer = new GraphicsLayer({ id: 'tasks-layer' });
    const highlightLayer = new GraphicsLayer({ id: 'highlight-layer' });

    webMap.addMany([graphicsLayer, highlightLayer]);
    
    graphicsLayerRef.current = graphicsLayer;
    highlightLayerRef.current = highlightLayer;
    viewRef.current = view;

    view.when(() => {
      setMapReady(true);
      
      // Handle click on map
      view.on('click', (event) => {
        view.hitTest(event).then((response) => {
          const graphicHit = response.results.find(
            result => result.graphic && result.graphic.layer === graphicsLayerRef.current
          );
          
          if (graphicHit && graphicHit.graphic.attributes) {
            const taskData = graphicHit.graphic.attributes;
            // Use ref to always get latest callback
            onSelectTaskRef.current(taskData);
          }
        });
      });

      // Change cursor on hover
      view.on('pointer-move', (event) => {
        view.hitTest(event).then((response) => {
          const hit = response.results.find(
            result => result.graphic && result.graphic.layer === graphicsLayerRef.current
          );
          view.container.style.cursor = hit ? 'pointer' : 'default';
        });
      });
    });

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, []); // Empty deps - map initialization should only run once

  // Create symbol based on status - memoized to avoid recreating on every render
  const createSymbol = useCallback((estado, isSelected = false) => {
    const config = getStatusFromEstado(estado);
    const size = isSelected ? 22 : 14;
    
    return new SimpleMarkerSymbol({
      style: config.shape,
      color: config.color,
      size: size,
      outline: {
        color: isSelected ? [255, 215, 0] : [255, 255, 255],
        width: isSelected ? 4 : 2
      }
    });
  }, []); // No dependencies - function is pure and getStatusFromEstado is imported


  // Update tasks on map
  useEffect(() => {
    if (!mapReady || !graphicsLayerRef.current) return;

    graphicsLayerRef.current.removeAll();

    if (!tasks || tasks.length === 0) {
      console.log('[MapView] No tasks to display');
      return;
    }

    console.log(`[MapView] Processing ${tasks.length} tasks for map display`);

    const graphics = tasks.map(task => {
      if (!task.geoLatitud || !task.geoLongitud) {
        console.warn('[MapView] Task without coordinates:', task.codigo);
        return null;
      }

      const lat = parseFloat(task.geoLatitud);
      const lng = parseFloat(task.geoLongitud);
      
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('[MapView] Invalid coordinates:', task.codigo, lat, lng);
        return null;
      }

      const isSelected = selectedTask && selectedTask.id === task.id;

      const point = new Point({
        longitude: lng,
        latitude: lat
      });

      const symbol = createSymbol(task.estado_reparacion || task.estado || task.DesEstadoObra, isSelected);

      return new Graphic({
        geometry: point,
        symbol: symbol,
        attributes: {
          id: task.id,
          codigo: task.codigo,
          titulo: task.titulo || task.descripcion || 'Sin título',
          descripcion: task.descripcion,
          estado: task.estado,
          prioridad: task.prioridad,
          direccion: task.direccion,
          fecha: task.fecha,
          geoLatitud: task.geoLatitud,
          geoLongitud: task.geoLongitud
        }
      });
    }).filter(Boolean);

    console.log(`[MapView] Adding ${graphics.length} graphics to map`);
    graphicsLayerRef.current.addMany(graphics);

    // Fit bounds to show all points if there are filtered tasks
    if (graphics.length > 0 && viewRef.current) {
      const extent = graphics.reduce((ext, graphic) => {
        const point = graphic.geometry;
        if (!ext) {
          return {
            xmin: point.longitude,
            xmax: point.longitude,
            ymin: point.latitude,
            ymax: point.latitude
          };
        }
        return {
          xmin: Math.min(ext.xmin, point.longitude),
          xmax: Math.max(ext.xmax, point.longitude),
          ymin: Math.min(ext.ymin, point.latitude),
          ymax: Math.max(ext.ymax, point.latitude)
        };
      }, null);

      // Only adjust view if we have a reasonable extent
      if (extent && (extent.xmax - extent.xmin > 0.001 || extent.ymax - extent.ymin > 0.001)) {
        viewRef.current.goTo({
          center: [(extent.xmin + extent.xmax) / 2, (extent.ymin + extent.ymax) / 2],
          zoom: 14
        }, { duration: 500 }).catch((error) => {
          if (error.name !== 'AbortError' && error.name !== 'view:goto-interrupted') {
            console.error('[MapView] goTo error:', error);
          }
        });
      }
    }
  }, [tasks, mapReady, selectedTask, createSymbol]); // Added createSymbol dependency

  // Handle selected task highlight
  useEffect(() => {
    if (!mapReady || !highlightLayerRef.current || !viewRef.current) return;

    highlightLayerRef.current.removeAll();

    if (selectedTask && selectedTask.geoLatitud && selectedTask.geoLongitud) {
      const lat = parseFloat(selectedTask.geoLatitud);
      const lng = parseFloat(selectedTask.geoLongitud);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Create highlight ring (outer glow)
        const glowPoint = new Point({ longitude: lng, latitude: lat });
        const glowSymbol = new SimpleMarkerSymbol({
          style: 'circle',
          color: [0, 0, 0, 0],
          size: 40,
          outline: {
            color: [255, 215, 0, 0.6],
            width: 6
          }
        });
        const glowGraphic = new Graphic({
          geometry: glowPoint,
          symbol: glowSymbol
        });

        // Create pulsing ring
        const pulseSymbol = new SimpleMarkerSymbol({
          style: 'circle',
          color: [0, 0, 0, 0],
          size: 50,
          outline: {
            color: [0, 200, 100, 0.4],
            width: 3
          }
        });
        const pulseGraphic = new Graphic({
          geometry: glowPoint,
          symbol: pulseSymbol
        });

        highlightLayerRef.current.addMany([pulseGraphic, glowGraphic]);

        // Animate to selected task
        viewRef.current.goTo({
          center: [lng, lat],
          zoom: Math.max(viewRef.current.zoom, 15)
        }, {
          duration: 500,
          easing: 'ease-in-out'
        }).catch((error) => {
          if (error.name !== 'AbortError' && error.name !== 'view:goto-interrupted') {
            console.error('[MapView] goTo error:', error);
          }
        });
      }
    }

    // Re-render tasks layer to update selected marker
    if (graphicsLayerRef.current && tasks) {
      graphicsLayerRef.current.removeAll();
      
      const graphics = tasks.map(task => {
        if (!task.geoLatitud || !task.geoLongitud) return null;

        const lat = parseFloat(task.geoLatitud);
        const lng = parseFloat(task.geoLongitud);
        
        if (isNaN(lat) || isNaN(lng)) return null;

        const isSelected = selectedTask && selectedTask.id === task.id;

        const point = new Point({
          longitude: lng,
          latitude: lat
        });

        const symbol = createSymbol(task.estado, isSelected);

        return new Graphic({
          geometry: point,
          symbol: symbol,
          attributes: {
            id: task.id,
            codigo: task.codigo,
            titulo: task.titulo || task.descripcion || 'Sin título',
            descripcion: task.descripcion,
            estado: task.estado,
            prioridad: task.prioridad,
            direccion: task.direccion,
            fecha: task.fecha,
            geoLatitud: task.geoLatitud,
            geoLongitud: task.geoLongitud
          }
        });
      }).filter(Boolean);

      graphicsLayerRef.current.addMany(graphics);
    }
  }, [selectedTask, mapReady, tasks, createSymbol]); // Added createSymbol dependency

  return (
    <div className="relative w-full h-full">
      <div
        ref={mapDiv}
        className="w-full h-full rounded-lg"
        data-testid="map-view"
        style={{ minHeight: '400px' }}
      />
      
      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 text-xs">
        <h4 className="font-semibold mb-2 text-sm text-gray-900 dark:text-gray-100">Leyenda</h4>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 border border-white shadow"></div>
            <span className="text-gray-700 dark:text-gray-300">Abierta</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rotate-45 bg-orange-500 border border-white shadow" style={{transform: 'rotate(45deg)'}}></div>
            <span className="text-gray-700 dark:text-gray-300">En Progreso / Reparación</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 border border-white shadow"></div>
            <span className="text-gray-700 dark:text-gray-300">Pendiente</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#008040] border border-white shadow"></div>
            <span className="text-gray-700 dark:text-gray-300">Resuelta / Completada</span>
          </div>
        </div>
        {tasks && (
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
            Mostrando {tasks.length} incidencia{tasks.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}
